import { useTranslation } from 'react-i18next';
import { AlertTriangle, Link2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { effectiveAnalyticsSyncStatus, isNonActiveAnalyticsConnection } from '@/lib/social-analytics/connectionState';
import { formatAnalyticsSyncDate } from '@/lib/social-analytics/formatAnalyticsSyncDate';
import type { AnalyticsConnection } from './AccountFilters';

type AnalyticsStatusBannerProps = {
  connections: readonly AnalyticsConnection[];
  onManageConnection: (connection: AnalyticsConnection) => void;
  showStaleFallback?: boolean;
};

export const AnalyticsStatusBanner = ({
  connections,
  onManageConnection,
  showStaleFallback = false,
}: AnalyticsStatusBannerProps) => {
  const { t, i18n } = useTranslation();
  const affectedConnections = connections.filter((connection) => (
    effectiveAnalyticsSyncStatus(connection) === 'error' || effectiveAnalyticsSyncStatus(connection) === 'reconnect_required'
  ));

  if (affectedConnections.length === 0 && !showStaleFallback) return null;

  const reconnectConnection = affectedConnections.find((connection) => (
    effectiveAnalyticsSyncStatus(connection) === 'reconnect_required'
  ));

  return (
    <section
      role="alert"
      aria-live="polite"
      className="flex flex-col gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 text-sm sm:flex-row sm:items-start sm:justify-between"
    >
      <div className="flex min-w-0 gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-300" />
        <div className="min-w-0 space-y-1">
          <p className="font-medium text-foreground">
            {reconnectConnection ? t('analytics.status.reconnectRequired.title') : t('analytics.status.stale.title')}
          </p>
          <p className="text-muted-foreground">
            {reconnectConnection ? t('analytics.status.reconnectRequired.description') : t('analytics.status.stale.description')}
          </p>
          <TooltipProvider delayDuration={120}>
            <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
              {affectedConnections.map((connection) => {
                const lastSuccess = formatAnalyticsSyncDate(
                  connection.analytics_last_success_at,
                  i18n.language.startsWith('pt') ? 'pt-BR' : 'en-US',
                  t('analytics.status.noSuccessfulSync'),
                );
                return (
                  <li key={connection.id} className="flex items-center gap-1.5">
                    <span className="font-medium text-foreground">@{connection.username}</span>
                    {isNonActiveAnalyticsConnection(connection) && (
                      <span>{t('analytics.status.nonActive')}</span>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="cursor-help underline decoration-dotted underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          aria-label={t('analytics.status.cachedDataHint')}
                        >
                          {t('analytics.status.lastSuccess', { date: lastSuccess })}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{t('analytics.status.cachedDataHint')}</TooltipContent>
                    </Tooltip>
                  </li>
                );
              })}
            </ul>
          </TooltipProvider>
        </div>
      </div>
      {reconnectConnection ? (
        <Button type="button" size="sm" variant="outline" className="shrink-0 border-amber-500/40" onClick={() => onManageConnection(reconnectConnection)}>
          <Link2 className="h-4 w-4" />
          {t('analytics.status.reconnect')}
        </Button>
      ) : (
        <span className="inline-flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
          <RefreshCw className="h-3.5 w-3.5" />
          {t('analytics.status.automaticRetry')}
        </span>
      )}
    </section>
  );
};
